import { cn } from "@babackapp/ui/lib/utils";
import { Column, Heading, Hr, Img, Row, Section, Text } from "@react-email/components";
import React from "react";
import type {
  ReturnLineItem,
  ReturnLineItemResolution,
} from "@babackapp/return-variables-registry";
import useThemeContextualColors, {
  sectionToParent,
} from "../../hooks/use-theme-contextual-colors";
import { useEmailThemeRadius } from "../../hooks";
import { tLayout } from "../../translations";
import { useReturnVariableValues } from "../../hooks/use-return-variable-values";
import type { ReturnLineItemsBlockProps } from "./types";

const SECTION_LABEL: Partial<Record<ReturnLineItemResolution, { key: string; fallback: string }>> = {
  refund: { key: "returnLineItems.refund", fallback: "Refund" },
  exchange: { key: "returnLineItems.exchange", fallback: "Exchange" },
  discount: { key: "returnLineItems.discount", fallback: "Discount" },
};

function getSubtitle(
  item: ReturnLineItem,
  currency: string,
  hidePrices?: boolean,
): string {
  switch (item.resolution) {
    case "refund": {
      if (hidePrices) return "";
      const price = item.paidPrice ?? item.price;
      return `${price} ${currency}`.trim();
    }
    case "exchange": {
      const original = item.variant?.title ?? "";
      const next = [
        item.newVariant?.option1,
        item.newVariant?.option2,
        item.newVariant?.option3,
      ]
        .filter(Boolean)
        .join(" ");
      if (!original && !next) return "";
      return `${original} → ${next}`;
    }
    default:
      return "";
  }
}

function LineItem({
  item,
  currency,
  textColor,
  hidePrices,
}: {
  item: ReturnLineItem;
  currency: string;
  textColor: string;
  hidePrices?: boolean;
}) {
  const name = item.name;
  const imageUrl = item.imageUrl ?? "#";
  const subtitle = getSubtitle(item, currency, hidePrices);

  return (
    <Row>
      <Column className="w-[64px] p-1">
        <Img
          src={imageUrl}
          width={64}
          height={64}
          alt={name}
          className={cn("w-[64px] h-[64px] rounded-lg object-cover")}
        />
      </Column>
      <Column className="pl-2">
        <Text
          className="m-0 p-0 leading-[1.4] font-semibold"
          style={{ color: textColor }}
        >
          {name} x {item.quantity}
        </Text>
        {subtitle && (
          <Text
            className="m-0 p-0 leading-[1.4] opacity-60"
            style={{ color: textColor }}
          >
            {subtitle}
          </Text>
        )}
      </Column>
    </Row>
  );
}

function ItemsContainer({
  children,
  contrastColor,
  theme,
}: React.PropsWithChildren & {
  contrastColor: string;
  theme: ReturnLineItemsBlockProps["theme"];
}) {
  const radius = useEmailThemeRadius(theme, "packageContentTable");

  return (
    <Section className={radius}>
      <Row
        className={cn(radius, "border-1 border-solid")}
        style={{ borderWidth: 1, borderColor: contrastColor }}
      >
        <Column>{children}</Column>
      </Row>
    </Section>
  );
}

export function ReturnLineItemsEmailRenderer({
  block,
  theme,
  section,
  locale,
  defaultLocale,
}: ReturnLineItemsBlockProps) {
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const { getReturnItems, getRefund } = useReturnVariableValues();
  const t = tLayout(locale, defaultLocale);

  const items = getReturnItems();
  const { currency } = getRefund();
  const hidePrices = block.properties?.hidePrices;

  if (!items.length) return null;

  const heading = (resolution: ReturnLineItemResolution) => {
    const entry = SECTION_LABEL[resolution];
    if (!entry) return resolution;
    const label = t(entry.key);
    return label === entry.key ? entry.fallback : label;
  };

  // Build display groups — matching the old React-email templates:
  //  • "exchange" and "exchange-credit" are merged under the "exchange" heading
  //  • Items without a known resolution are order articles not in the return, hidden
  const displayGroups = ([
    {
      resolution: "refund" as ReturnLineItemResolution,
      items: items.filter((item) => item.resolution === "refund"),
    },
    {
      resolution: "exchange" as ReturnLineItemResolution,
      // exchange-credit items are grouped with exchange (same section, same heading)
      items: items.filter((item) =>
        item.resolution === "exchange" || item.resolution === "exchange-credit",
      ),
    },
    {
      resolution: "discount" as ReturnLineItemResolution,
      items: items.filter((item) => item.resolution === "discount"),
    },
  ] as { resolution: ReturnLineItemResolution; items: ReturnLineItem[] }[]).filter(
    (group) => group.items.length > 0,
  );

  return (
    <Section>
      {displayGroups.map((group) => (
        <Section key={group.resolution} className="mb-2">
          <Heading as="h5" className="m-0 mb-2 text-xs font-bold text-secondary-foreground">
            {heading(group.resolution)}
          </Heading>
          <ItemsContainer theme={theme} contrastColor={colors.contrast}>
            {group.items.map((item, index) => (
              <React.Fragment key={`${group.resolution}-${index}`}>
                <LineItem
                  item={item}
                  currency={currency}
                  textColor={colors.text}
                  hidePrices={hidePrices}
                />
                {index < group.items.length - 1 && (
                  <Hr
                    className="border"
                    style={{ borderColor: colors.contrast }}
                  />
                )}
              </React.Fragment>
            ))}
          </ItemsContainer>
        </Section>
      ))}
    </Section>
  );
}
